import 'uplot/dist/uPlot.min.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { FlightReport, LogChartsResponse } from '../types';
import {
  AgentChart, ChartLayout, Inventory, LayoutRecord, PlotSpec, SERIES_DRAG_TYPE, SeriesRef, Tab, ViewContext,
} from './layoutTypes';
import { applyLayout, sameRef, seriesName, toSavable } from './applyLayout';
import {
  analystPlots, buildMarkers, buildModeBands, flightEnd, overviewPlots,
} from './builtinTabs';
import {
  createLayout, deleteLayout, fetchInventory, listLayouts, setDefaultLayout, updateLayout,
} from './seriesClient';
import { useTabData } from './useTabData';
import ExplorerPlot, { fmtClock, parseDraggedRef } from './ExplorerPlot';
import ProfileBar from './ProfileBar';
import SeriesTree from './SeriesTree';
import { buttonClass } from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import Skeleton from '../../components/ui/Skeleton';
import Reveal from '../../components/ui/Reveal';
import Appear from '../../components/ui/Appear';
import FadeIn from '../../components/ui/FadeIn';

export interface ExplorerProps {
  /** '' when no log or a multi-log selection: shows the empty state. */
  logId: string;
  logCharts: LogChartsResponse | null;
  logChartsLoading: boolean;
  report: FlightReport | null;
  agentCharts: AgentChart[];
  focusTime: number | null;
  onFocusTimeChange: (t: number | null) => void;
  onViewChange: (view: ViewContext) => void;
}

const OVERVIEW = 'Overview';
const ANALYST = 'Analyst';
const RESERVED = [OVERVIEW, ANALYST];
const MAX_PLOTS = 24;
const MAX_SERIES = 16;
const MIN_PLOT_WIDTH = 300;
const GAP_PX = 8;
const HEIGHTS: Record<1 | 2 | 3, number> = { 1: 260, 2: 220, 3: 180 };
const GRID_COLS: Record<1 | 2 | 3, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' };
const EMPTY_PLOTS: PlotSpec[] = [];

let explorerSeq = 0;

const blankLayout = (): ChartLayout => ({ version: 1, tabs: [{ name: 'Plots', columns: 2, plots: [] }] });

const newPlotId = () => `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const uniqueName = (wanted: string, taken: string[]): string => {
  const base = wanted.trim().slice(0, 40) || 'Plots';
  const clash = (n: string) => taken.includes(n) || RESERVED.includes(n);
  if (!clash(base)) return base;
  for (let i = 2; ; i += 1) {
    const n = `${base.slice(0, 36)} ${i}`;
    if (!clash(n)) return n;
  }
};

/** A stored layout as the working copy: the person's tabs only, never empty. */
const fromRecord = (layout: ChartLayout | undefined): ChartLayout => {
  const tabs = (layout?.tabs || []).filter(t => !t.builtin && !RESERVED.includes(t.name));
  return tabs.length ? { version: 1, tabs } : blankLayout();
};

const errText = (err: any) => err?.message || 'the server did not answer';

const listNames = (refs: SeriesRef[]) => {
  const names = refs.map(seriesName);
  return names.length > 6 ? `${names.slice(0, 6).join(', ')} and ${names.length - 6} more` : names.join(', ');
};

const Explorer: React.FC<ExplorerProps> = ({
  logId, logCharts, logChartsLoading, report, agentCharts, focusTime, onFocusTimeChange, onViewChange,
}) => {
  const api = useApi();
  // useApi returns a new object every render; requests read the latest through a ref.
  const apiRef = useRef(api);
  apiRef.current = api;
  const onFocusTimeRef = useRef(onFocusTimeChange);
  onFocusTimeRef.current = onFocusTimeChange;
  const onViewRef = useRef(onViewChange);
  onViewRef.current = onViewChange;

  const syncKey = useRef(`explorer-${(explorerSeq += 1)}`).current;
  const fieldsId = `${syncKey}-fields`;

  /* ── Profiles and the working layout ── */

  const [profiles, setProfiles] = useState<LayoutRecord[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [activeProfile, setActiveProfile] = useState<LayoutRecord | null>(null);
  const [working, setWorking] = useState<ChartLayout>(blankLayout);
  const [activeTab, setActiveTab] = useState<string>(OVERVIEW);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const touched = useRef(false);

  const latest = useRef({ working, activeTab, focusedId });
  latest.current = { working, activeTab, focusedId };

  const applyProfile = useCallback((r: LayoutRecord | null) => {
    const next = r ? fromRecord(r.layout) : blankLayout();
    setActiveProfile(r);
    setWorking(next);
    setActiveTab(next.tabs[0].name);
    setFocusedId(null);
    setStatus(null);
  }, []);

  useEffect(() => {
    let alive = true;
    listLayouts(apiRef.current)
      .then(l => {
        if (!alive) return;
        setProfiles(l.layouts);
        setDefaultId(l.default_id);
        const d = l.layouts.find(r => r.id === l.default_id);
        // Someone who started building before the list arrived keeps what they built.
        if (d && !touched.current) applyProfile(d);
      })
      .catch(() => { if (alive) setStatus('Saved profiles did not load. Reload the page to try again.'); });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const editWorking = useCallback((fn: (l: ChartLayout) => ChartLayout) => {
    touched.current = true;
    setStatus(null);
    setWorking(fn);
  }, []);

  const dirty = useMemo(() => {
    const baseline = activeProfile ? fromRecord(activeProfile.layout) : blankLayout();
    return JSON.stringify(toSavable(working)) !== JSON.stringify(toSavable(baseline));
  }, [working, activeProfile]);

  /* ── The log: inventory, window ── */

  const [inv, setInv] = useState<{ data: Inventory | null; loading: boolean; error: string | null }>(
    { data: null, loading: false, error: null },
  );
  const [invNonce, setInvNonce] = useState(0);
  const retryInventory = useCallback(() => setInvNonce(n => n + 1), []);
  useEffect(() => {
    if (!logId) { setInv({ data: null, loading: false, error: null }); return undefined; }
    let alive = true;
    setInv({ data: null, loading: true, error: null });
    fetchInventory(apiRef.current, logId)
      .then(data => { if (alive) setInv({ data, loading: false, error: null }); })
      .catch(err => {
        if (alive) setInv({ data: null, loading: false, error: `The field list did not load: ${errText(err)}.` });
      });
    return () => { alive = false; };
  }, [logId, invNonce]);

  const [window, setWindow] = useState<[number, number] | null>(null);
  const [missingDismissedFor, setMissingDismissedFor] = useState<string | null>(null);
  useEffect(() => { setWindow(null); }, [logId]);

  const onWindowChange = useCallback((w: [number, number] | null) => {
    setWindow(w ? [Math.min(w[0], w[1]), Math.max(w[0], w[1])] : null);
  }, []);

  /* ── Tabs as drawn ── */

  const applied = useMemo(
    () => (inv.data ? applyLayout(working, inv.data) : { layout: working, missing: [] as SeriesRef[] }),
    [working, inv.data],
  );

  const strippedIds = useMemo(() => {
    const ids = new Set<string>();
    working.tabs.forEach((t, ti) => t.plots.forEach((p, pi) => {
      if (p.series.length > 0 && applied.layout.tabs[ti]?.plots[pi]?.series.length === 0) ids.add(p.id);
    }));
    return ids;
  }, [working, applied]);

  const overview = useMemo(() => overviewPlots(logCharts), [logCharts]);
  const analyst = useMemo(() => analystPlots(agentCharts), [agentCharts]);

  const allTabs = useMemo<Tab[]>(() => [
    { name: OVERVIEW, columns: 2, plots: overview, builtin: 'overview' },
    { name: ANALYST, columns: 2, plots: analyst, builtin: 'analyst' },
    ...applied.layout.tabs,
  ], [overview, analyst, applied]);

  const tab = allTabs.find(t => t.name === activeTab) || allTabs[0];
  const editable = !tab.builtin;

  // A chart the model just drew is what the person asked about: show it.
  const analystCount = useRef(agentCharts.length);
  useEffect(() => {
    if (agentCharts.length > analystCount.current) setActiveTab(ANALYST);
    analystCount.current = agentCharts.length;
  }, [agentCharts.length]);

  /* ── Grid size ── */

  const gridRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);
  useEffect(() => {
    const el = gridRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setGridWidth(Math.round(el.clientWidth / 25) * 25));
    ro.observe(el);
    setGridWidth(Math.round(el.clientWidth / 25) * 25);
    return () => ro.disconnect();
  }, [logId]);

  const columns = (gridWidth > 0
    ? Math.max(1, Math.min(tab.columns, Math.floor((gridWidth + GAP_PX) / (MIN_PLOT_WIDTH + GAP_PX))))
    : tab.columns) as 1 | 2 | 3;
  const plotWidth = gridWidth > 0 ? (gridWidth - GAP_PX * (columns - 1)) / columns : 600;

  /* ── Data ── */

  const ready = !!inv.data || !!inv.error;
  const fetchPlots = logId && ready ? tab.plots : EMPTY_PLOTS;
  const { data, retry } = useTabData(api, logId, fetchPlots, window, plotWidth);

  const modeBands = useMemo(
    () => buildModeBands(report?.timeline, flightEnd(logCharts, report?.identity?.duration_s)),
    [report, logCharts],
  );
  const markers = useMemo(() => buildMarkers(report?.checks), [report]);

  /* ── Pointer: the page's cursor is drawn only while no plot here owns it ── */

  // The last time a plot hovered: that value needs no line (uPlot's own cursor shows it),
  // a pick from the report or the chat does, even with the pointer over the grid.
  const lastHover = useRef<number | null>(null);
  const onHover = useCallback((t: number | null) => { lastHover.current = t; onFocusTimeRef.current(t); }, []);

  /* ── Edits ── */

  const updatePlot = useCallback((id: string, fn: (p: PlotSpec) => PlotSpec | null) => {
    editWorking(l => ({
      ...l,
      tabs: l.tabs.map(t => (t.plots.some(p => p.id === id)
        ? { ...t, plots: t.plots.flatMap(p => { if (p.id !== id) return [p]; const n = fn(p); return n ? [n] : []; }) }
        : t)),
    }));
  }, [editWorking]);

  const onTitleChange = useCallback((id: string, title: string) => {
    updatePlot(id, p => ({ ...p, title: title.slice(0, 80) }));
  }, [updatePlot]);

  const onRemoveSeries = useCallback((id: string, ref: SeriesRef) => {
    updatePlot(id, p => ({ ...p, series: p.series.filter(s => !sameRef(s, ref)) }));
  }, [updatePlot]);

  const onRemovePlot = useCallback((id: string) => {
    updatePlot(id, () => null);
    setFocusedId(f => (f === id ? null : f));
  }, [updatePlot]);

  const onDropSeries = useCallback((id: string, ref: SeriesRef) => {
    updatePlot(id, p => {
      if (p.series.some(s => sameRef(s, ref))) return p;
      if (p.series.length >= MAX_SERIES) {
        // Counts saved series this log lacks too: they come back on another log.
        setStatus(`${p.title || 'This plot'} holds ${MAX_SERIES} series. Add a plot for more.`);
        return p;
      }
      return { ...p, series: [...p.series, ref] };
    });
    setFocusedId(id);
  }, [updatePlot]);

  const onFocusPlot = useCallback((id: string) => setFocusedId(id), []);

  /** A new plot in the active person tab, or the first person tab when a built-in one is showing. */
  const addPlot = useCallback((ref: SeriesRef | null) => {
    const { working: w, activeTab: at } = latest.current;
    const target = w.tabs.find(t => t.name === at) || w.tabs[0];
    const name = target ? target.name : uniqueName('Plots', []);
    if (target && target.plots.length >= MAX_PLOTS) {
      setStatus(`${name} holds ${MAX_PLOTS} plots. Add a tab for more.`);
      setActiveTab(name);
      return;
    }
    const id = newPlotId();
    const plot: PlotSpec = { id, series: ref ? [ref] : [] };
    editWorking(prev => (prev.tabs.some(t => t.name === name)
      ? { ...prev, tabs: prev.tabs.map(t => (t.name === name ? { ...t, plots: [...t.plots, plot] } : t)) }
      : { ...prev, tabs: [...prev.tabs, { name, columns: 2, plots: [plot] }] }));
    setActiveTab(name);
    setFocusedId(id);
  }, [editWorking]);

  const onAdd = useCallback((ref: SeriesRef) => {
    const { working: w, activeTab: at, focusedId: fid } = latest.current;
    const current = w.tabs.find(t => t.name === at);
    const target = current && fid ? current.plots.find(p => p.id === fid) : undefined;
    if (target && !target.inline) {
      if (target.series.some(s => sameRef(s, ref))) {
        setStatus(`${seriesName(ref)} is already on ${target.title || 'this plot'}.`);
        return;
      }
      if (target.series.length < MAX_SERIES) {
        onDropSeries(target.id, ref);
        return;
      }
    }
    addPlot(ref);
  }, [addPlot, onDropSeries]);

  const onSelectTab = useCallback((name: string) => setActiveTab(name), []);

  const onAddTab = useCallback(() => {
    const name = uniqueName('Plots', latest.current.working.tabs.map(t => t.name));
    editWorking(l => ({ ...l, tabs: [...l.tabs, { name, columns: 2, plots: [] }] }));
    setActiveTab(name);
    setFocusedId(null);
  }, [editWorking]);

  const onRenameTab = useCallback((from: string, to: string) => {
    const name = uniqueName(to, latest.current.working.tabs.map(t => t.name).filter(n => n !== from));
    editWorking(l => ({ ...l, tabs: l.tabs.map(t => (t.name === from ? { ...t, name } : t)) }));
    setActiveTab(a => (a === from ? name : a));
  }, [editWorking]);

  const onRemoveTab = useCallback((name: string) => {
    const tabs = latest.current.working.tabs;
    const i = tabs.findIndex(t => t.name === name);
    const rest = tabs.filter(t => t.name !== name);
    editWorking(l => ({ ...l, tabs: l.tabs.filter(t => t.name !== name) }));
    setActiveTab(a => (a === name ? (rest[Math.max(0, i - 1)]?.name || OVERVIEW) : a));
  }, [editWorking]);

  const onColumnsChange = useCallback((name: string, cols: 1 | 2 | 3) => {
    editWorking(l => ({ ...l, tabs: l.tabs.map(t => (t.name === name ? { ...t, columns: cols } : t)) }));
  }, [editWorking]);

  /* ── Profile actions ── */

  const savable = useCallback(() => {
    const out = toSavable(latest.current.working);
    if (out.tabs.length === 0) {
      setStatus('Add a tab before saving. A profile holds at least one.');
      return null;
    }
    return out;
  }, []);

  const onSave = useCallback(async () => {
    if (!activeProfile?.mine) return;
    const layout = savable();
    if (!layout) return;
    setBusy(true);
    try {
      const rec = await updateLayout(apiRef.current, activeProfile.id, { layout });
      setActiveProfile(rec);
      setProfiles(ps => ps.map(p => (p.id === rec.id ? rec : p)));
      setStatus(null);
    } catch (err) {
      setStatus(`${activeProfile.name} did not save: ${errText(err)}. Your changes are still here.`);
    } finally {
      setBusy(false);
    }
  }, [activeProfile, savable]);

  const onSaveAs = useCallback(async (name: string, shared: boolean) => {
    const layout = savable();
    if (!layout) return;
    setBusy(true);
    try {
      const rec = await createLayout(apiRef.current, { name, shared, layout });
      setProfiles(ps => [...ps, rec]);
      setActiveProfile(rec);
      setStatus(null);
    } catch (err) {
      setStatus(`${name} did not save: ${errText(err)}. Your changes are still here.`);
    } finally {
      setBusy(false);
    }
  }, [savable]);

  const onSetDefault = useCallback(async () => {
    if (!activeProfile) return;
    const id = defaultId === activeProfile.id ? null : activeProfile.id;
    setBusy(true);
    try {
      const r = await setDefaultLayout(apiRef.current, id);
      setDefaultId(r?.default_id ?? id);
      setStatus(null);
    } catch (err) {
      setStatus(`The default did not change: ${errText(err)}.`);
    } finally {
      setBusy(false);
    }
  }, [activeProfile, defaultId]);

  const onDelete = useCallback(async () => {
    if (!activeProfile?.mine) return;
    const gone = activeProfile;
    setBusy(true);
    try {
      await deleteLayout(apiRef.current, gone.id);
      setProfiles(ps => ps.filter(p => p.id !== gone.id));
      setDefaultId(d => (d === gone.id ? null : d));
      // The plots stay on screen as an unsaved view, so a slip is one Save as away from undone.
      setActiveProfile(null);
      setStatus(null);
    } catch (err) {
      setStatus(`${gone.name} was not deleted: ${errText(err)}.`);
    } finally {
      setBusy(false);
    }
  }, [activeProfile]);

  const onPickProfile = useCallback((r: LayoutRecord | null) => {
    touched.current = true;
    applyProfile(r);
  }, [applyProfile]);

  const onToggleFields = useCallback(() => setFieldsOpen(o => !o), []);

  /* ── What the model is told the person is looking at ── */

  useEffect(() => {
    const timer = setTimeout(() => {
      onViewRef.current({
        profile: activeProfile?.name ?? null,
        tab: tab.name,
        window,
        plots: tab.plots.slice(0, 24).map(p => ({
          title: (p.title || 'Untitled').slice(0, 80),
          series: (p.inline ? Object.keys(p.inline.series) : p.series.map(seriesName)).slice(0, 16).map(n => n.slice(0, 80)),
        })),
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [activeProfile, tab, window]);

  /* ── Render ── */

  const focusedPlot = editable && focusedId ? tab.plots.find(p => p.id === focusedId) : undefined;
  const focusedPlotTitle = focusedPlot ? (focusedPlot.title || focusedPlot.series[0]?.topic || 'the new plot') : null;
  const showMissing = !!logId && applied.missing.length > 0 && missingDismissedFor !== logId;
  const height = HEIGHTS[columns];

  const [addDragOver, setAddDragOver] = useState(false);
  const addZoneDrop = {
    onDragOver: (e: React.DragEvent) => {
      if (!Array.from(e.dataTransfer.types).includes(SERIES_DRAG_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (!addDragOver) setAddDragOver(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setAddDragOver(false);
    },
    onDrop: (e: React.DragEvent) => {
      setAddDragOver(false);
      const ref = parseDraggedRef(e.dataTransfer.getData(SERIES_DRAG_TYPE));
      if (!ref) return;
      e.preventDefault();
      addPlot(ref);
    },
  };

  const emptyBuiltin = (() => {
    if (tab.builtin === 'overview') {
      if (logChartsLoading && !logCharts) return null;
      if (!logCharts) return 'The overview did not load for this log. Your own tabs still work.';
      if (tab.plots.length === 0) return 'This log has no overview charts.';
    }
    if (tab.builtin === 'analyst' && tab.plots.length === 0) {
      return 'Charts the analyst draws land here. Ask about this flight to get one.';
    }
    return null;
  })();

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-2">
      <ProfileBar
        tabs={allTabs}
        activeTab={tab.name}
        onSelectTab={onSelectTab}
        onAddTab={onAddTab}
        onRenameTab={onRenameTab}
        onRemoveTab={onRemoveTab}
        onColumnsChange={onColumnsChange}
        profiles={profiles}
        activeProfile={activeProfile}
        dirty={dirty}
        defaultId={defaultId}
        onPickProfile={onPickProfile}
        onSave={onSave}
        onSaveAs={onSaveAs}
        onSetDefault={onSetDefault}
        onDelete={onDelete}
        onToggleFields={onToggleFields}
        fieldsOpen={fieldsOpen && !!logId}
        fieldsId={fieldsId}
        status={status}
        busy={busy}
      />

      <Reveal show={!!(showMissing || (window && logId))}>
        <div className="flex min-w-0 flex-col gap-1 px-1 text-xs text-on-surface-variant">
          <Reveal show={showMissing}>
          {showMissing && (
            <div className="flex min-w-0 items-center gap-2">
              <p className="min-w-0 truncate" title={listNames(applied.missing)}>
                {applied.missing.length === 1
                  ? `1 series is not in this log: ${listNames(applied.missing)}`
                  : `${applied.missing.length} series are not in this log: ${listNames(applied.missing)}`}
              </p>
              <button
                type="button"
                onClick={() => setMissingDismissedFor(logId)}
                aria-label="Dismiss the missing series note"
                className={buttonClass('quiet', 'sm')}
              >
                Dismiss
              </button>
            </div>
          )}
          </Reveal>
          <Reveal show={!!(window && logId)}>
          {window && logId && (
            <div className="flex min-w-0 items-center gap-2">
              <p className="min-w-0 truncate font-mono tabular-nums">
                Zoomed to {fmtClock(window[0], true)} to {fmtClock(window[1], true)}
              </p>
              <button type="button" onClick={() => setWindow(null)} className={buttonClass('quiet', 'sm')}>
                Show the whole flight
              </button>
            </div>
          )}
          </Reveal>
        </div>
      </Reveal>

      <FadeIn key={logId ? 'log' : 'none'} className="flex min-h-0 min-w-0 flex-1 flex-col">
      {!logId ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1 rounded-lg bg-surface-container-low px-6 text-center">
          <p className="text-sm font-medium text-on-surface">Pick a log to explore it</p>
          <p className="text-xs text-on-surface-variant">Plots, profiles and the overview appear here once a single log is selected.</p>
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 gap-2">
          {fieldsOpen && (
            <SeriesTree
              id={fieldsId}
              inventory={inv.data}
              loading={inv.loading}
              error={inv.error}
              onAdd={onAdd}
              focusedPlotTitle={focusedPlotTitle}
              onRetry={retryInventory}
            />
          )}
          <div
            ref={gridRef}
            className="dt-scroll min-h-0 min-w-0 flex-1 overflow-y-auto"
          >
            <Reveal show={tab.builtin === 'overview' && logChartsLoading && !logCharts}>
            {tab.builtin === 'overview' && logChartsLoading && !logCharts && (
              <div className={`grid ${GRID_COLS[columns]} gap-2`} aria-label="Loading the overview">
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className="rounded-lg bg-surface-container-low p-3">
                    <Skeleton className="mb-3 h-3 w-32 rounded" />
                    <div style={{ height }}><Skeleton className="h-full w-full rounded" /></div>
                  </div>
                ))}
              </div>
            )}
            </Reveal>

            <Reveal show={!!emptyBuiltin}>
              <EmptyState>{emptyBuiltin}</EmptyState>
            </Reveal>

            {tab.plots.length > 0 && (
              <div className={`grid ${GRID_COLS[columns]} gap-2`}>
                {tab.plots.map(p => (
                  <ExplorerPlot
                    key={p.id}
                    plot={p}
                    data={p.inline ? undefined : data[p.id]}
                    window={window}
                    onWindowChange={onWindowChange}
                    modeBands={modeBands}
                    markers={markers}
                    focusTime={focusTime != null && focusTime === lastHover.current ? null : focusTime}
                    onHover={onHover}
                    syncKey={syncKey}
                    height={height}
                    editable={editable}
                    focused={editable && p.id === focusedId}
                    missingInLog={strippedIds.has(p.id)}
                    onTitleChange={editable ? onTitleChange : undefined}
                    onRemoveSeries={editable ? onRemoveSeries : undefined}
                    onRemovePlot={editable ? onRemovePlot : undefined}
                    onDropSeries={editable ? onDropSeries : undefined}
                    onFocus={editable ? onFocusPlot : undefined}
                    onRetry={retry}
                  />
                ))}
              </div>
            )}

            {editable && (
              <div
                {...addZoneDrop}
                className={`mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-lg border border-dashed border-outline px-4 text-center ${addDragOver ? 'bg-primary/10' : 'bg-surface-container-low'} ${tab.plots.length ? 'py-3' : 'py-12'}`}
              >
                <p className="text-sm text-on-surface-variant">
                  {addDragOver
                    ? 'Drop to start a new plot'
                    : tab.plots.length
                      ? 'Drop a field here for a new plot.'
                      : 'Click a field to plot it, or drag one here.'}
                </p>
                {!addDragOver && (
                  <span className="flex items-center gap-1">
                    <Appear show={!fieldsOpen}>
                      <button type="button" onClick={onToggleFields} aria-controls={fieldsId} className={buttonClass('quiet')}>
                        Open fields
                      </button>
                    </Appear>
                    <Appear show={tab.plots.length < MAX_PLOTS}>
                      <button type="button" onClick={() => addPlot(null)} className={buttonClass('quiet')}>
                        Add an empty plot
                      </button>
                    </Appear>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      </FadeIn>
    </div>
  );
};

export default Explorer;
